import { apiClient as api } from './api';
import {
  UrbanAgentStartRequest,
  UrbanAgentIterateRequest,
  UrbanAgentIterateResponse,
  UrbanAgentType,
  UrbanAgentSession
} from '../models/urbanAgents';

const URBAN_SESSION_KEY = 'urban_agent_sessions';

export const urbanAgentService = {
  // Start session endpoints
  async startAnimationSession(request: UrbanAgentStartRequest): Promise<UrbanAgentSession> {
    const apiRequest = {
      initialPrompt: request.prompt,
      animationStyle: request.style,
      urbanEra: request.era,
      urbanRegion: request.region,
      maxResponseTokens: request.maxResponseTokens,
      temperature: request.temperature
    };
    const response = await api.post('/api/imageprompt/urban/animation/session/start', apiRequest);
    return {
      sessionId: response.data.sessionId,
      agentType: UrbanAgentType.Animation,
      currentPrompt: response.data.initialPrompt,
      iterations: [],
      metadata: response.data
    };
  },

  async startDocumentarySession(request: UrbanAgentStartRequest): Promise<UrbanAgentSession> {
    const apiRequest = {
      subject: request.prompt,
      documentaryStyle: request.style,
      era: request.era,
      location: request.region,
      maxResponseTokens: request.maxResponseTokens,
      temperature: request.temperature
    };
    const response = await api.post('/api/imageprompt/urban/documentary/session/start', apiRequest);
    return {
      sessionId: response.data.sessionId,
      agentType: UrbanAgentType.Documentary,
      currentPrompt: response.data.generatedPrompt || response.data.subject || response.data.initialPrompt || request.prompt,
      iterations: [],
      metadata: response.data
    };
  },

  async startMusicSession(request: UrbanAgentStartRequest): Promise<UrbanAgentSession> {
    // For now, assume music endpoint uses similar fields
    const apiRequest = {
      initialPrompt: request.prompt,
      style: request.style,
      era: request.era,
      region: request.region,
      maxResponseTokens: request.maxResponseTokens,
      temperature: request.temperature
    };
    const response = await api.post('/api/imageprompt/urban/music/session/start', apiRequest);
    return {
      sessionId: response.data.sessionId,
      agentType: UrbanAgentType.Music,
      currentPrompt: response.data.initialPrompt || request.prompt,
      iterations: [],
      metadata: response.data
    };
  },

  async startStreetArtSession(request: UrbanAgentStartRequest): Promise<UrbanAgentSession> {
    // Street art endpoint requires InitialConcept field
    const apiRequest = {
      InitialConcept: request.prompt,
      style: request.style,
      era: request.era,
      location: request.region,
      maxResponseTokens: request.maxResponseTokens,
      temperature: request.temperature
    };
    const response = await api.post('/api/imageprompt/urban/streetart/session/start', apiRequest);
    return {
      sessionId: response.data.sessionId,
      agentType: UrbanAgentType.StreetArt,
      currentPrompt: response.data.initialPrompt || request.prompt,
      iterations: [],
      metadata: response.data
    };
  },

  async startGeneralSession(request: UrbanAgentStartRequest): Promise<UrbanAgentSession> {
    const apiRequest = {
      initialPrompt: request.prompt,
      style: request.style,
      era: request.era,
      region: request.region,
      maxResponseTokens: request.maxResponseTokens,
      temperature: request.temperature
    };
    const response = await api.post('/api/imageprompt/urban/session/start', apiRequest);
    return {
      sessionId: response.data.sessionId,
      agentType: UrbanAgentType.General,
      currentPrompt: response.data.initialPrompt || request.prompt,
      iterations: [],
      metadata: response.data
    };
  },

  // Iterate endpoints
  async iterateAnimation(sessionId: string, request: UrbanAgentIterateRequest): Promise<UrbanAgentIterateResponse> {
    const apiRequest = {
      agentType: 'Animation',
      focusElement: request.feedback || request.style,
      maxResponseTokens: request.maxResponseTokens,
      temperature: request.temperature
    };
    const response = await api.post(`/api/imageprompt/urban/animation/session/${sessionId}/iterate`, apiRequest);
    return response.data;
  },

  async iterateDocumentary(sessionId: string, request: UrbanAgentIterateRequest): Promise<UrbanAgentIterateResponse> {
    // Assume similar structure for documentary iterate
    const apiRequest = {
      agentType: 'Documentary',
      focusElement: request.feedback,
      maxResponseTokens: request.maxResponseTokens,
      temperature: request.temperature
    };
    const response = await api.post(`/api/imageprompt/urban/documentary/session/${sessionId}/iterate`, apiRequest);
    return response.data;
  },

  async iterateMusic(sessionId: string, request: UrbanAgentIterateRequest): Promise<UrbanAgentIterateResponse> {
    const apiRequest = {
      agentType: 'Music',
      focusElement: request.feedback,
      maxResponseTokens: request.maxResponseTokens,
      temperature: request.temperature
    };
    const response = await api.post(`/api/imageprompt/urban/music/session/${sessionId}/iterate`, apiRequest);
    return response.data;
  },

  async iterateStreetArt(sessionId: string, request: UrbanAgentIterateRequest): Promise<UrbanAgentIterateResponse> {
    const apiRequest = {
      agentType: 'StreetArt',
      focusElement: request.feedback,
      maxResponseTokens: request.maxResponseTokens,
      temperature: request.temperature
    };
    const response = await api.post(`/api/imageprompt/urban/streetart/session/${sessionId}/iterate`, apiRequest);
    return response.data;
  },

  async iterateGeneral(sessionId: string, request: UrbanAgentIterateRequest): Promise<UrbanAgentIterateResponse> {
    const apiRequest = {
      agentType: 'General',
      focusElement: request.feedback,
      maxResponseTokens: request.maxResponseTokens,
      temperature: request.temperature
    };
    const response = await api.post(`/api/imageprompt/urban/session/${sessionId}/iterate`, apiRequest);
    return response.data;
  },

  // Special endpoints
  async getAnimationGlossary(): Promise<any> {
    const response = await api.get('/api/imageprompt/urban/animation/animation-glossary');
    return response.data;
  },

  async getCulturalGlossary(): Promise<any> {
    const response = await api.get('/api/imageprompt/urban/cultural-glossary');
    return response.data;
  },

  async generateConcertScene(data: any): Promise<any> {
    const response = await api.post('/api/imageprompt/urban/music/concert-scene', data);
    return response.data;
  },

  async generateStreetArtPiece(data: any): Promise<any> {
    const response = await api.post('/api/imageprompt/urban/streetart/generate-piece', data);
    return response.data;
  },

  // Session management
  saveSessionToLocalStorage(sessionId: string, sessionData: any): void {
    const sessions = this.getSessionsFromLocalStorage();
    sessions[sessionId] = {
      ...sessionData,
      lastUpdated: new Date().toISOString()
    };
    localStorage.setItem(URBAN_SESSION_KEY, JSON.stringify(sessions));
  },

  getSessionsFromLocalStorage(): Record<string, any> {
    const stored = localStorage.getItem(URBAN_SESSION_KEY);
    return stored ? JSON.parse(stored) : {};
  },

  getSessionFromLocalStorage(sessionId: string): any {
    const sessions = this.getSessionsFromLocalStorage();
    return sessions[sessionId];
  },

  deleteSessionFromLocalStorage(sessionId: string): void {
    const sessions = this.getSessionsFromLocalStorage();
    delete sessions[sessionId];
    localStorage.setItem(URBAN_SESSION_KEY, JSON.stringify(sessions));
  },

  getAllSessionIds(): string[] {
    const sessions = this.getSessionsFromLocalStorage();
    return Object.keys(sessions);
  }
};